import { PublisherWebhookEventName, PublisherWebhookEventTypes } from "@/lib/consts/publisher";
import { httpClient } from '@/lib/http-client';
import { getUtmParams, convertUtmParamsForWebhooks } from "./utm";
import { getStoreDomain } from "./domain-client";
import coralogixRumService from "../services/coralogixRum";
import { LoggerEventNames } from "../consts/logger";

const PUBLISHER_DOMAIN_HEADER = 'x-publisher-domain';

const PUBLISHER_WEBHOOK_V2_EVENT_TYPES = new Set<string>(
    Object.values(PublisherWebhookEventTypes),
);

interface PublisherWebhookEventProps {
    eventName: PublisherWebhookEventName;
    eventType: PublisherWebhookEventTypes;
}

// shared-ui still calls this with the v1 type "offer". That path is /store/events/offer.
// Posting it under /store/events/v2/ 404s; the v2 call uses "offers".
export const isPublisherWebhookV2EventType = (
    eventType: string,
): eventType is PublisherWebhookEventTypes =>
    PUBLISHER_WEBHOOK_V2_EVENT_TYPES.has(eventType);

export const sendPublisherWebhookEvent = async (eventType: string, props: Record<string, unknown>) => {
    if (!isPublisherWebhookV2EventType(eventType)) {
        return;
    }

    const utmParams = convertUtmParamsForWebhooks(getUtmParams());
    const storeDomain = getStoreDomain();
    const headers = storeDomain
        ? { [PUBLISHER_DOMAIN_HEADER]: storeDomain }
        : undefined;

    return httpClient.post(`/store/events/v2/${eventType}`, {
        ...utmParams,
        ...props,
    }, {
        keepalive: true,
        ...(headers ? { headers } : {}),
    });
}

export const sendPublisherWebhookEvents = async (props: PublisherWebhookEventProps, eventData: Record<string, unknown>) => {
    const { eventName, eventType } = props;
    const { getCustomEventSharedProps } = await import('./events');
    // UA-CH resolves in a microtask; awaiting it here means even the first
    // webhook (post-login) carries the full device model/version info.
    const { warmFullDeviceInfo } = await import('./fullDeviceInfo');
    await warmFullDeviceInfo();

    sendPublisherWebhookEvent(eventType, {
        eventName,
        ...eventData,
        ...getCustomEventSharedProps(),
    }).catch((error) => {
        coralogixRumService.sendErrorEvent(LoggerEventNames.PUBLISHER_WEBHOOK_ERROR, {
            error: error
        });
    });
}
